const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  if (typeof value?._seconds === 'number') return new Date(value._seconds * 1000);
  if (typeof value?.milliseconds === 'number') return new Date(value.milliseconds);

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getAssignedIds = (value) =>
  Array.isArray(value) ? value.filter(Boolean) : [];

const getFirstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null);

const getRowDate = (row) => row.effectiveFrom || row.mainEventerAssignedAt || null;

const getSnapshotSignature = (row) => [
  getRowDate(row)?.getTime() || '',
  row.champion || '',
  row.inRing.join(','),
  row.backstage.join(','),
  row.mainEventerExpiresAt?.getTime() || ''
].join('|');

const normalizeLineupHistoryRow = (snapshot, source) => {
  if (!snapshot) return null;

  const nestedLineup = snapshot.lineup || snapshot.currentLineup || snapshot.snapshot || {};
  const champion = getFirstDefined(
    snapshot.champion,
    snapshot.championId,
    snapshot.mainEventer,
    snapshot.mainEventerId,
    nestedLineup.champion,
    nestedLineup.championId,
    nestedLineup.mainEventer,
    nestedLineup.mainEventerId,
    null
  );
  const inRing = getAssignedIds(
    getFirstDefined(
      snapshot.inRing,
      snapshot.inRingIds,
      nestedLineup.inRing,
      nestedLineup.inRingIds,
      []
    )
  );
  const backstage = getAssignedIds(
    getFirstDefined(
      snapshot.backstage,
      snapshot.backstageIds,
      nestedLineup.backstage,
      nestedLineup.backstageIds,
      []
    )
  );
  const isEmpty = !champion && inRing.length === 0 && backstage.length === 0;

  return {
    source,
    effectiveFrom: toDate(
      getFirstDefined(
        snapshot.effectiveFrom,
        snapshot.effectiveAt,
        snapshot.createdAt,
        nestedLineup.effectiveFrom,
        nestedLineup.effectiveAt,
        nestedLineup.createdAt
      )
    ),
    mainEventerAssignedAt: toDate(
      getFirstDefined(
        snapshot.mainEventerAssignedAt,
        snapshot.assignedAt,
        nestedLineup.mainEventerAssignedAt,
        nestedLineup.assignedAt
      )
    ),
    mainEventerExpiresAt: toDate(
      getFirstDefined(
        snapshot.mainEventerExpiresAt,
        snapshot.expiresAt,
        nestedLineup.mainEventerExpiresAt,
        nestedLineup.expiresAt
      )
    ),
    champion,
    inRing,
    backstage,
    isCurrent: source === 'current',
    isEmpty,
  };
};

export const buildFactionLineupHistoryRows = (faction) => {
  if (!faction) return [];

  const rowsBySignature = new Map();
  const addRow = (row) => {
    if (!row) return;

    const signature = getSnapshotSignature(row);
    const existingRow = rowsBySignature.get(signature);

    if (existingRow) {
      rowsBySignature.set(signature, {
        ...existingRow,
        isCurrent: existingRow.isCurrent || row.isCurrent,
      });
      return;
    }

    rowsBySignature.set(signature, row);
  };

  if (Array.isArray(faction.lineupHistory)) {
    faction.lineupHistory.forEach((snapshot) => {
      addRow(normalizeLineupHistoryRow(snapshot, 'history'));
    });
  }

  addRow(normalizeLineupHistoryRow(faction.currentLineup, 'current'));

  if (rowsBySignature.size === 0) {
    addRow(normalizeLineupHistoryRow(faction.lineup, 'legacy'));
  }

  return [...rowsBySignature.values()]
    .sort(
      (a, b) =>
        (getRowDate(b)?.getTime() ?? Number.NEGATIVE_INFINITY) -
        (getRowDate(a)?.getTime() ?? Number.NEGATIVE_INFINITY)
    )
    .map((row, index) => ({
      ...row,
      key: `${getSnapshotSignature(row)}-${index}`,
    }));
};
